import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { EmailImpersonationService } from '../services/email-impersonation.service';
import { reloadIntoMailbox } from '../mailbox-reload.util';
import { IMPERSONATE_AS } from './impersonation-context';

/**
 * Stamps `X-Impersonate-User-Id` onto Gmail requests while an owner is switched
 * into a rep's mailbox. The backend's `resolveMailbox` middleware reads it,
 * authorizes the owner, and points every mailbox operation at that rep.
 *
 * Scope is deliberately narrow:
 *  - Only requests to the Node API's `gmail/` namespace are touched.
 *  - The owner-context `auth/*` endpoints are EXCLUDED — connecting, listing
 *    accounts/staff, switching and disconnecting always run as the logged-in
 *    owner. `auth/status` is deliberately NOT excluded: while impersonating, the
 *    status (and its reported email) must reflect the rep's mailbox so the
 *    reply-to-self logic and header identity are correct.
 */
@Injectable()
export class EmailImpersonationInterceptor implements HttpInterceptor {
  private readonly gmailBase = `${environment.nodeApiUrl}gmail/`;
  /** Owner-context endpoints that must always run as the logged-in owner. */
  private readonly ownerContextPaths = [
    'auth/accounts',
    'auth/staff-mailboxes',
    'auth/switch',
    'auth/disconnect',
    'auth/initiate',
    'auth/connect',
    'auth/callback',
  ];

  constructor(private impersonation: EmailImpersonationService) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // A request may pin the mailbox it belongs to. Only Undo Send does today: it
    // is built now and sent thirty seconds later, by which time the live target
    // may be a different rep entirely. `undefined` means "not pinned" — every
    // other caller — and `null` explicitly means the owner's own mailbox, so it
    // must suppress the header rather than fall through to the live value.
    const pinned = req.context.get(IMPERSONATE_AS);
    const targetId = pinned === undefined ? this.impersonation.targetId : pinned;
    if (targetId != null && req.url.startsWith(this.gmailBase)) {
      const sub = req.url.slice(this.gmailBase.length); // e.g. 'threads?…' or 'auth/status'
      const isOwnerContext = this.ownerContextPaths.some((p) => sub.startsWith(p));
      if (!isOwnerContext) {
        return next.handle(req.clone({ setHeaders: { 'X-Impersonate-User-Id': String(targetId) } })).pipe(
          catchError((err: unknown) => {
            // A 403 on a request carrying this header can only be resolveMailbox
            // refusing the impersonation: the target stopped being an active user,
            // or this account is no longer on the owner allowlist.
            //
            // The target lives in sessionStorage and is rehydrated on every boot, so
            // without this the whole mailbox stays wedged — `auth/status` 403s, the
            // module reports "We could not check your mailbox connection", and no
            // amount of reloading or reconnecting clears it, because connecting is
            // owner-context and never touches the stale target. Drop it and fall back
            // to the owner's own mailbox, which is always a valid place to be.
            //
            // 409 is the same dead end one step later: resolveMailbox accepted the
            // target, but the rep has no Gmail session any more (disconnected, or
            // their tokens were revoked) — `getConnectedUserId` answers 409
            // MAILBOX_NOT_CONNECTED on every route. The stored target can never
            // work again, and the only other 409 the Gmail API sends is on
            // `auth/disconnect`, which is owner-context and never carries this
            // header, so the status alone identifies it.
            if (err instanceof HttpErrorResponse && (err.status === 403 || err.status === 409)) {
              this.impersonation.clear();
              reloadIntoMailbox();
            }
            return throwError(() => err);
          }),
        );
      }
    }
    return next.handle(req);
  }
}
